import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const ACADEMY_CART_STORAGE_KEY = 'espotz-academy-cart';

/** Max modules per checkout — mirrors the backend MAX_ACADEMY_CART_MODULE_IDS guard. */
export const MAX_ACADEMY_CART_MODULES = 5;

/**
 * Persisted cart line. The cart stores identity only — every display field (name, price,
 * tier, expiry) is hydrated live from the catalog + cart-preview APIs, so nothing shown
 * in the cart can go stale when an admin edits a module.
 */
export interface AcademyCartEntry {
  moduleId: string;
  addedAt: number;
}

const cartListeners = new Set<() => void>();
let cachedCartEntries: AcademyCartEntry[] = [];

const isAcademyCartEntry = (value: unknown): value is AcademyCartEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as AcademyCartEntry;
  return (
    typeof entry.moduleId === 'string' && typeof entry.addedAt === 'number'
  );
};

/** Pre-refactor carts persisted full module snapshots keyed by `moduleKey`; keep just identity. */
const fromLegacyCartItem = (value: unknown): AcademyCartEntry | null => {
  if (!value || typeof value !== 'object') return null;
  const legacy = value as { moduleKey?: unknown; addedAt?: unknown };
  if (typeof legacy.moduleKey !== 'string' || legacy.moduleKey === '') {
    return null;
  }
  return {
    moduleId: legacy.moduleKey,
    addedAt: typeof legacy.addedAt === 'number' ? legacy.addedAt : Date.now(),
  };
};

const readCartFromStorage = (): AcademyCartEntry[] => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(ACADEMY_CART_STORAGE_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const entries = parsed
      .map(value =>
        isAcademyCartEntry(value) ? value : fromLegacyCartItem(value),
      )
      .filter((entry): entry is AcademyCartEntry => entry !== null);

    // Dedupe (first add wins) — legacy carts keyed lines by tier:module, not moduleId.
    const seen = new Set<string>();
    return entries.filter(entry => {
      if (seen.has(entry.moduleId)) return false;
      seen.add(entry.moduleId);
      return true;
    });
  } catch {
    return [];
  }
};

const refreshCachedCartEntries = (): AcademyCartEntry[] => {
  const next = readCartFromStorage();
  // Keep the snapshot referentially stable when contents are unchanged —
  // useSyncExternalStore re-renders on every new array reference.
  if (
    next.length !== cachedCartEntries.length ||
    next.some(
      (entry, index) =>
        entry.moduleId !== cachedCartEntries[index]?.moduleId ||
        entry.addedAt !== cachedCartEntries[index]?.addedAt,
    )
  ) {
    cachedCartEntries = next;
  }
  return cachedCartEntries;
};

const emitCartChange = () => {
  refreshCachedCartEntries();
  cartListeners.forEach(listener => listener());
};

const writeCartToStorage = (entries: AcademyCartEntry[]) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      ACADEMY_CART_STORAGE_KEY,
      JSON.stringify(entries),
    );
  }
  emitCartChange();
};

refreshCachedCartEntries();

export const formatAcademyModulePrice = (
  amount: number,
  currency: string,
): string => {
  const normalizedCurrency = currency.trim() || 'EC';
  return `${amount.toLocaleString('en-IN')} ${normalizedCurrency}`;
};

export const formatAcademyEcAmount = (amount: number): string =>
  formatAcademyModulePrice(amount, 'EC');

/** Parses an optional raw EC price string (e.g. `AcademyModuleItem.discountedPrice`). Undefined/blank/non-finite values yield `undefined`. */
export const parseOptionalEcPrice = (
  raw?: string | null,
): number | undefined => {
  const trimmed = raw?.trim() ?? '';
  if (trimmed === '') return undefined;

  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const subscribeAcademyCart = (listener: () => void): (() => void) => {
  // Re-sync from storage when a consumer mounts — another tab or a pre-refactor
  // write may have changed it since this module loaded.
  refreshCachedCartEntries();
  cartListeners.add(listener);
  return () => {
    cartListeners.delete(listener);
  };
};

export const getAcademyCartSnapshot = (): AcademyCartEntry[] =>
  cachedCartEntries;

/** Clears persisted academy cart — call on logout so the next session cannot see prior items. */
export const clearAcademyCart = (): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(ACADEMY_CART_STORAGE_KEY);
  }
  emitCartChange();
};

export const useAcademyCart = () => {
  const entries = useSyncExternalStore(
    subscribeAcademyCart,
    getAcademyCartSnapshot,
    () => [],
  );

  const { t } = useTranslation('academy');

  // Returns false (and toasts) when the cart is already full and this is a new module,
  // so callers can skip navigating to checkout. Adding a module already in the cart is a no-op success.
  const addItem = useCallback(
    (moduleId: string): boolean => {
      const current = getAcademyCartSnapshot();
      if (current.some(entry => entry.moduleId === moduleId)) {
        return true;
      }
      if (current.length >= MAX_ACADEMY_CART_MODULES) {
        toast.error(t('cart.maxModules', { max: MAX_ACADEMY_CART_MODULES }));
        return false;
      }
      writeCartToStorage([...current, { moduleId, addedAt: Date.now() }]);
      return true;
    },
    [t],
  );

  const removeItem = useCallback((moduleId: string) => {
    writeCartToStorage(
      readCartFromStorage().filter(entry => entry.moduleId !== moduleId),
    );
  }, []);

  return {
    entries,
    count: entries.length,
    addItem,
    removeItem,
  };
};
